import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

import { SearchItemType, Movie, Star, User, getMovieTags, getMovieGenres } from '@usheru-hff/shared/domain';
import { TrackingService } from '@usheru-hff/sdk/tracking';

import { MovieSuggestionItemComponent } from './movie-suggestion-item/movie-suggestion-item.component';
import { StarSuggestionItemComponent } from './star-suggestion-item/star-suggestion-item.component';
import { UserSuggestionItemComponent } from './user-suggestion-item/user-suggestion-item.component';
import { SearchMovieFilterType } from '../shared/entities/search-movie-filter.type';
import { SearchMovieService } from '../shared/search-movie.service';
import { isMovie, isUser } from '../shared/search-movie.utils';

@Component({
  selector: 'search-suggestion-item',
  standalone: true,
  imports: [MovieSuggestionItemComponent, StarSuggestionItemComponent, UserSuggestionItemComponent],
  styleUrl: './search-suggestion-item.component.scss',
  templateUrl: './search-suggestion-item.component.html'
})
export class SearchSuggestionItemComponent implements OnInit {
  private readonly searchService = inject(SearchMovieService);

  private readonly trackingService = inject(TrackingService);

  private readonly router = inject(Router);

  @Input({ required: true }) item!: SearchItemType;

  @Output() itemSelect = new EventEmitter<SearchItemType>();

  itemType: SearchMovieFilterType = null;

  ngOnInit() {
    this.setupItemType(this.item);
  }

  handleItemSelect(item: SearchItemType) {
    this.searchService.setSelectedItem(item);

    this.trackClickEvent(item);

    if (this.searchService.isRedirectEnabled()) {
      this.redirectToItemDetail(item);
      this.itemSelect.emit(item);
    }
  }

  trackClickEvent(item: SearchItemType) {
    switch (this.itemType) {
      case 'movie':
        const movie = item as Movie;

        this.trackingService.trackEvent({
          action: 'SelectMovie',

          properties: {
            category: 'MovieFilters',

            label: movie.title,

            movie: movie.title,

            idMovie: movie.id,

            imdb: movie?.imdb,

            tagsMovie: getMovieTags(movie),
            genresMovie: getMovieGenres(movie),

            source: 'mainMenu'
          }
        });

        break;

      case 'star':
        const star = item as Star;
        this.trackingService.trackEvent({
          action: 'SelectTalent',
          properties: {
            category: 'MovieFilters',
            label: star.name,
            star: star.name,
            idStar: star.id,
            source: 'mainMenu'
          }
        });

        break;

      case 'user':
        const user = item as User;
        this.trackingService.trackEvent({
          action: 'SelectUser',
          properties: {
            category: 'MovieFilters',
            label: user.name,
            user: user.username,
            source: 'mainMenu'
          }
        });

        break;
    }
  }

  redirectToItemDetail(item: SearchItemType) {
    switch (this.itemType) {
      case 'movie':
        this.router.navigate(['movie', (item as Movie).friendlyUrl]);

        break;

      case 'user':
        this.router.navigate(['user-activity', (item as User).username]);

        break;

      case 'star':
        this.router.navigate(['talent', (item as Star).slug]);

        break;
    }
  }

  private setupItemType(item: SearchItemType) {
    if (isMovie(item)) {
      this.itemType = 'movie';
    } else if (isUser(item)) {
      this.itemType = 'user';
    } else {
      this.itemType = 'star';
    }
  }
}
